/**
 * Abschnitt Kanäle — Shopify-Status, Push (Online-Preis + Bestand) und
 * Veröffentlichung. Aus ProductCardSheet übernommen; nur mit gespeichertem Stand.
 */
import { useState } from 'react';
import { CheckCircle2, CircleDashed, ExternalLink, Store } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/pdfRenderer';
import { pushOperationalData, publishProductToShopify } from '@/lib/shopifyPush';
import { Section } from './EditorPrimitives';

export const shopifyAdminUrl = (gid) => {
  const id = (gid || '').split('/').pop();
  return id ? `https://admin.shopify.com/store/inmotion-handel/products/${id}` : null;
};

function ChannelRow({ ok, name, sub, right }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm">
      <div className="flex items-center gap-2 min-w-0">
        {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <CircleDashed className="w-4 h-4 text-slate-400 shrink-0" />}
        <div className="min-w-0">
          <p className="font-medium truncate">{name}</p>
          <p className="text-[11px] text-slate-400 truncate">{sub}</p>
        </div>
      </div>
      {right}
    </div>
  );
}

export default function ProductEditorKanaele({ product, variants, dirty, onPublished }) {
  const [pushing, setPushing] = useState(false);
  const active = (variants || []).filter((v) => v.aktiv !== false);
  const adminUrl = shopifyAdminUrl(product?.shopify_product_id);
  const synced = product?.content_synced_at ? formatDate(product.content_synced_at) : '—';

  const push = async () => {
    setPushing(true);
    try {
      const res = await pushOperationalData(product, active);
      const fails = res.filter((r) => !r.ok);
      if (fails.length === 0) toast.success(`Preis & Bestand gepusht (${res.length} SKU)`);
      else toast.error(`${fails.length}/${res.length} fehlgeschlagen: ${fails[0].message}`);
    } catch (err) {
      toast.error('Push fehlgeschlagen: ' + err.message);
    } finally { setPushing(false); }
  };

  const publish = async () => {
    if (!window.confirm(`„${product.name}“ jetzt in Shopify veröffentlichen? Das Produkt wird im Online-Shop sichtbar (und via Shopify auch bei eBay).`)) return;
    setPushing(true);
    try {
      await publishProductToShopify(product, active);
      toast.success('In Shopify veröffentlicht ✓');
      onPublished?.();
    } catch (err) {
      toast.error(err.message);
    } finally { setPushing(false); }
  };

  return (
    <Section icon={Store} title="Kanäle"
      hint="Online-Preis, Verfügbar-Bestand, Gewicht und Grundpreis gehen nach Shopify. B2B-Preise, Kundenpreise und EWK-Daten bleiben in FieldConnect.">
      <div className="space-y-2">
        <ChannelRow ok={!!product.channel_shopify} name="Shopify (inmotion-handel.de)"
          sub={product.channel_shopify ? `Verbunden · Sync: ${synced}` : 'Noch nicht veröffentlicht'}
          right={adminUrl && (
            <a href={adminUrl} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-brand-950 inline-flex items-center gap-1 min-h-[44px] shrink-0">
              Öffnen <ExternalLink className="w-3 h-3" />
            </a>
          )} />
        <ChannelRow ok={!!product.channel_shopify} name="eBay" sub="über Shopify verbunden — folgt automatisch" />
      </div>
      {dirty && <p className="text-[11px] text-amber-700">Erst speichern, dann pushen — sonst gehen alte Werte in den Shop.</p>}
      {product.channel_shopify ? (
        <Button type="button" onClick={push} disabled={pushing || dirty} className="w-full min-h-[44px] bg-brand-950 hover:bg-brand-900">
          {pushing ? 'Wird gepusht…' : 'Online-Preis & Bestand jetzt pushen'}
        </Button>
      ) : (
        <div className="space-y-2">
          <Button type="button" onClick={publish} disabled={pushing || dirty} className="w-full min-h-[44px] bg-brand-950 hover:bg-brand-900">
            {pushing ? 'Wird veröffentlicht…' : 'In Shopify veröffentlichen'}
          </Button>
          <p className="text-[11px] text-slate-400">Voraussetzung: SKU + Online-Preis in den Varianten.</p>
        </div>
      )}
    </Section>
  );
}
